/**
 * split-thickness.js
 *
 * Companion to snippets/variant-split-thickness.liquid.
 * Drives the split Finish / Thickness pill selectors for products whose
 * Finish option values follow the "{Finish} {dash} {Thickness}mm" pattern.
 * Detection is automatic (see split-thickness-detect.liquid) and both the
 * en-dash (" – ") and plain hyphen (" - ") delimiters are supported.
 *
 * Selection priority is Thickness > Width > Finish > Backing (customers
 * pick thickness first):
 *   - Thickness pills are always enabled for any thickness that has an
 *     available variant in ANY width/finish.
 *   - Width pills cross out when the selected thickness doesn't come in
 *     that width; clicking a thickness auto-jumps width/finish to a real
 *     variant instead of dead-ending on "Unavailable".
 *   - Finish pills filter by the selected thickness + width.
 *   - Backing (the option after the split one) stays on the theme's own
 *     availability logic, which already filters by everything above it.
 *
 * The theme's native combined picker stays in the DOM (hidden). This script
 * matches pills to the native combined radio by PARSED PAIR (finish +
 * thickness); checking the native radio and firing a change event lets
 * theme.Variants handle variant matching, price/SKU/image/URL updates and
 * cart submission natively.
 *
 * Uses document-level event delegation so it also works for markup injected
 * after page load. Loads only on pages that render the split snippet.
 */
(function () {
  'use strict';

  if (window.__ledgeSplitThickness) return;
  window.__ledgeSplitThickness = true;

  var EN_DASH = ' – ';
  var HYPHEN = ' - ';

  // "Pre Finished – 0.5mm" -> {finish: "Pre Finished", thickness: "0.5mm"}
  // Falls back to the plain-hyphen delimiter; null if neither is present.
  function splitValue(value) {
    if (typeof value !== 'string') return null;
    var i = value.indexOf(EN_DASH);
    var delim = EN_DASH;
    if (i === -1) {
      i = value.indexOf(HYPHEN);
      delim = HYPHEN;
    }
    if (i === -1) return null;
    return {
      finish: value.slice(0, i),
      thickness: value.slice(i + delim.length)
    };
  }

  function getContainer(root) {
    return root.closest('[data-section-type="product"]') || document;
  }

  function getVariants(root) {
    if (!root.__stVariants) {
      var jsonEl = getContainer(root).querySelector('[data-variant-json]');
      try {
        root.__stVariants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
      } catch (err) {
        root.__stVariants = [];
      }
    }
    return root.__stVariants;
  }

  function getPills(root, part) {
    return Array.prototype.slice.call(
      root.querySelectorAll('input[data-split-pill="' + part + '"]')
    );
  }

  function getCheckedValue(root, part) {
    var checked = getPills(root, part).filter(function (input) {
      return input.checked;
    });
    return checked.length ? checked[0].value : null;
  }

  function setCheckedValue(root, part, value) {
    getPills(root, part).forEach(function (input) {
      input.checked = input.value === value;
    });
  }

  function getNativeRadios(root) {
    return Array.prototype.slice.call(
      root.querySelectorAll('.split-thickness__native input[data-variant-input]')
    );
  }

  function splitPosition(root) {
    return parseInt(root.dataset.optionPosition, 10);
  }

  // Native inputs of one PRECEDING option (e.g. Width when the split option
  // is option2), living outside this root in the same product container.
  function precedingInputs(root, position) {
    return Array.prototype.slice.call(
      getContainer(root).querySelectorAll(
        '.variant-input-wrap[data-index="option' + position + '"] input[data-variant-input]'
      )
    );
  }

  // Currently selected values of every option BEFORE the split option,
  // keyed by option position.
  function currentPreceding(root) {
    var container = getContainer(root);
    var values = {};
    for (var i = 1; i < splitPosition(root); i++) {
      var checked = container.querySelector(
        '.variant-input-wrap[data-index="option' + i + '"] input:checked'
      );
      var select = container.querySelector('select[data-index="option' + i + '"]');
      values[i] = checked ? checked.value : (select ? select.value : null);
    }
    return values;
  }

  /**
   * Find variants matching the given filters.
   * filters: {finish?, thickness?, preceding?: {position: value}}
   * requireAvailable: only count purchasable variants.
   */
  function queryVariants(root, filters, requireAvailable) {
    var optionIndex = root.dataset.optionIndex;

    return getVariants(root).filter(function (variant) {
      if (requireAvailable && !variant.available) return false;

      var parts = splitValue(variant[optionIndex]);
      if (!parts) return false;
      if (filters.finish != null && parts.finish !== filters.finish) return false;
      if (filters.thickness != null && parts.thickness !== filters.thickness) return false;

      if (filters.preceding) {
        for (var position in filters.preceding) {
          var value = filters.preceding[position];
          if (value != null && variant['option' + position] !== value) return false;
        }
      }
      return true;
    });
  }

  function setPillState(input, label, disabled) {
    input.classList.toggle('disabled', disabled);
    if (label) label.classList.toggle('disabled', disabled);
  }

  /**
   * Repaint availability, priority order Thickness > Width > Finish:
   * - thickness pills: enabled if the thickness is available anywhere
   * - preceding (Width) pills: filtered by selected thickness only
   * - finish pills: filtered by selected thickness + preceding selections
   * Runs after the theme's own availability pass and overrides the
   * preceding-option paint (the theme would enable all Width values).
   */
  function repaint(root) {
    var selectedThickness = getCheckedValue(root, 'thickness');
    var preceding = currentPreceding(root);

    getPills(root, 'thickness').forEach(function (input) {
      var ok = queryVariants(root, { thickness: input.value }, true).length > 0;
      setPillState(input, input.nextElementSibling, !ok);
    });

    for (var i = 1; i < splitPosition(root); i++) {
      precedingInputs(root, i).forEach(function (input) {
        var filters = { thickness: selectedThickness, preceding: {} };
        filters.preceding[i] = input.value;
        var ok = queryVariants(root, filters, true).length > 0;
        var label = getContainer(root).querySelector('label[for="' + input.id + '"]') || input.nextElementSibling;
        setPillState(input, label, !ok);
      });
    }

    getPills(root, 'finish').forEach(function (input) {
      var ok = queryVariants(root, {
        finish: input.value,
        thickness: selectedThickness,
        preceding: preceding
      }, true).length > 0;
      setPillState(input, input.nextElementSibling, !ok);
    });
  }

  // Mirror the hidden native combined selection back onto the pills
  // (the theme can silently re-check the native radio when dynamic
  // variants auto-correct after a width change).
  function syncFromNative(root) {
    var checked = getNativeRadios(root).filter(function (radio) {
      return radio.checked;
    })[0];
    if (!checked) return;

    var parts = splitValue(checked.value);
    if (!parts) return;

    setCheckedValue(root, 'finish', parts.finish);
    setCheckedValue(root, 'thickness', parts.thickness);
  }

  // Move the whole selection (preceding options + pills + native combined
  // radio) to the given variant, then let theme.Variants take over.
  function applyVariant(root, variant) {
    var optionIndex = root.dataset.optionIndex;
    var parts = splitValue(variant[optionIndex]);
    if (!parts) return;

    // Silently align preceding options (e.g. Width) — same as the theme's
    // own _updateInputState, which also sets checked without events.
    for (var i = 1; i < splitPosition(root); i++) {
      precedingInputs(root, i).forEach(function (input) {
        input.checked = input.value === variant['option' + i];
      });
    }

    setCheckedValue(root, 'finish', parts.finish);
    setCheckedValue(root, 'thickness', parts.thickness);

    var nativeRadio = getNativeRadios(root).filter(function (radio) {
      return radio.value === variant[optionIndex];
    })[0];

    if (nativeRadio) {
      nativeRadio.checked = true;
      // theme.Variants listens directly on the input; non-bubbling event
      // avoids re-triggering this script's document-level delegate.
      nativeRadio.dispatchEvent(new Event('change'));
    }

    repaint(root);
  }

  // First variant matching any of the filter cascades (most specific wins).
  function pickVariant(root, cascades) {
    for (var c = 0; c < cascades.length; c++) {
      var found = queryVariants(root, cascades[c].filters, cascades[c].available);
      if (found.length) return found[0];
    }
    return null;
  }

  function onPillChange(root, changedInput) {
    var changedPart = changedInput.dataset.splitPill;
    var finish = getCheckedValue(root, 'finish');
    var thickness = getCheckedValue(root, 'thickness');
    if (finish === null || thickness === null) return;

    var preceding = currentPreceding(root);
    var cascades;

    if (changedPart === 'thickness') {
      // Thickness wins: keep width, then finish, then jump whatever needed.
      cascades = [
        { filters: { thickness: thickness, finish: finish, preceding: preceding }, available: true },
        { filters: { thickness: thickness, preceding: preceding }, available: true },
        { filters: { thickness: thickness, finish: finish }, available: true },
        { filters: { thickness: thickness }, available: true },
        { filters: { thickness: thickness, finish: finish, preceding: preceding }, available: false },
        { filters: { thickness: thickness }, available: false }
      ];
    } else {
      // Finish clicked: keep thickness (higher priority), jump width if
      // needed; only change thickness when the finish exists nowhere else.
      cascades = [
        { filters: { finish: finish, thickness: thickness, preceding: preceding }, available: true },
        { filters: { finish: finish, thickness: thickness }, available: true },
        { filters: { finish: finish, preceding: preceding }, available: true },
        { filters: { finish: finish }, available: true },
        { filters: { finish: finish, thickness: thickness, preceding: preceding }, available: false },
        { filters: { finish: finish }, available: false }
      ];
    }

    var variant = pickVariant(root, cascades);
    if (!variant) return;

    applyVariant(root, variant);
  }

  document.addEventListener('change', function (evt) {
    var target = evt.target;
    if (!target || !target.matches) return;

    if (target.matches('input[data-split-pill]')) {
      var root = target.closest('[data-split-thickness-root]');
      if (root) onPillChange(root, target);
      return;
    }

    // A native option changed elsewhere (e.g. Width): re-sync every split
    // root in the same product section. Runs after the theme's handlers,
    // so the repaint overrides the theme's own Width availability paint.
    if (target.matches('[data-variant-input]')) {
      var container = target.closest('[data-section-type="product"]');
      if (!container) return;
      container.querySelectorAll('[data-split-thickness-root]').forEach(function (root) {
        syncFromNative(root);
        repaint(root);
      });
    }
  });

  function repaintAll() {
    document.querySelectorAll('[data-split-thickness-root]').forEach(function (root) {
      syncFromNative(root);
      repaint(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repaintAll);
  } else {
    repaintAll();
  }

  // The theme's own availability pass (which re-enables all Width values)
  // runs during its section init; repaint again after everything settled.
  window.addEventListener('load', repaintAll);

  // Re-init when sections re-render in the theme editor.
  document.addEventListener('shopify:section:load', repaintAll);
})();
